/* eslint-disable react/prop-types */
import React, { useState, useContext } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { useToasts } from "react-toast-notifications"
import { Formik } from "formik"
import * as Yup from "yup"
import moment from "moment"
import { customAlphabet } from "nanoid"
import { withFirebase } from "../../../utils/Firebase"
import CustomInput from "../../atoms/CustomInput"
import CustomSelect from "../../atoms/CustomSelect"
import Drawer from "../../atoms/Drawer"
import { StyledForm, SubmitButton } from "./styles"
import { AuthUserContext } from "../../../context/Session"

const nanoid = customAlphabet("1234567890ABCDEF", 8)

const promotionTypes = [
  { value: "basic", label: "Básico" },
  { value: "advanced", label: "Advanced" },
  { value: "premium", label: "Premium" },
  { value: "any", label: "Todo" },
]

const promotionAmountTypes = [
  { value: "fixed", label: "Monto fijo" },
  { value: "percentage", label: "Porcentual" },
]

const PromotionDrawer = ({ firebase, open, closeDrawer }) => {
  const { t } = useTranslation()
  const { addToast } = useToasts()
  const [isCreating, setIsCreating] = useState(false)
  const authUser = useContext(AuthUserContext)
  const [promotionType, setPromotionType] = useState(undefined)
  const [promotionAmountType, setPromotionAmountType] = useState(undefined)

  const getPromotionCode = async () => {
    let code = nanoid()
    let isUnique = await firebase.isUniquePromotionCode(code)
    while (!isUnique) {
      code = nanoid()
      // eslint-disable-next-line no-await-in-loop
      isUnique = await firebase.isUniquePromotionCode(code)
    }
    return code
  }

  return (
    <Drawer
      openDrawer={open}
      closeDrawer={closeDrawer}
      title={t("create_promotion")}
      internal
    >
      <div>
        <Formik
          initialValues={{
            createdBy: undefined,
            endDate: undefined,
            promotionDiscount: undefined,
            promotionNumberMonths: undefined,
            startDate: undefined,
            state: "active",
            type: "basic",
            promotionAmountType: "percentage",
          }}
          onSubmit={async (values, { resetForm }) => {
            setIsCreating(true)
            try {
              const promotionCode = await getPromotionCode()
              await firebase.savePromotion({
                promotionData: {
                  createdBy: authUser.email,
                  startDate: moment(values.startDate).startOf("day").valueOf(),
                  endDate: moment(values.endDate).endOf("day").valueOf(),
                  state: values.state,
                  promotionDiscount: values.promotionDiscount,
                  promotionNumberMonths: values.promotionNumberMonths,
                  type: values.type,
                  promotionCode: promotionCode.toUpperCase(),
                  promotionAmountType: values.promotionAmountType,
                },
              })

              addToast(t("saved_successfully"), {
                appearance: "success",
                placement: "bottom-center",
              })
              resetForm({ value: "" })
              setPromotionType(undefined)
              setPromotionAmountType(undefined)
              setIsCreating(false)
              closeDrawer()
            } catch (e) {
              setIsCreating(false)
            }
          }}
          validationSchema={Yup.object({
            type: Yup.string().required(t("error_all_fields_required")),
            startDate: Yup.date().required(t("error_all_fields_required")),
            endDate: Yup.date().required(t("error_all_fields_required")),
            promotionDiscount: Yup.number().required(
              t("error_all_fields_required")
            ),
            promotionNumberMonths: Yup.number().required(
              t("error_all_fields_required")
            ),
            promotionAmountType: Yup.string().required(
              t("error_all_fields_required")
            ),
          })}
        >
          {(props) => (
            <StyledForm onSubmit={props.handleSubmit}>
              <p>Ingrese los datos de la nueva promoción.</p>
              <CustomSelect
                value={promotionType}
                formKey="type"
                lngKey="promotion_plan_type"
                handleChange={(event, value) => {
                  props.setFieldValue("type", value)
                  setPromotionType(
                    promotionTypes.filter((p) => p.value === value)
                  )
                }}
                items={promotionTypes}
                showError={!props.values.type && Boolean(props.errors.type)}
              />
              <CustomInput
                touched={props.touched.startDate}
                errors={props.errors.startDate}
                icon="date"
                value={props.values.startDate}
                onChange={props.handleChange}
                onBlur={props.handleBlur}
                labelName={t("start_date")}
                type="date"
                id="startDate"
                name="startDate"
              />
              <CustomInput
                touched={props.touched.endDate}
                errors={props.errors.endDate}
                icon="date"
                value={props.values.endDate}
                onChange={props.handleChange}
                onBlur={props.handleBlur}
                labelName={t("end_date")}
                type="date"
                id="endDate"
                name="endDate"
              />
              <CustomSelect
                value={promotionAmountType}
                formKey="promotionAmountType"
                lngKey="promotion_type"
                handleChange={(event, value) => {
                  props.setFieldValue("promotionAmountType", value)
                  setPromotionAmountType(
                    promotionAmountTypes.filter((p) => p.value === value)
                  )
                }}
                items={promotionAmountTypes}
                showError={!props.values.type && Boolean(props.errors.type)}
              />
              <CustomInput
                touched={props.touched.promotionDiscount}
                errors={props.errors.promotionDiscount}
                value={props.values.promotionDiscount}
                onChange={props.handleChange}
                onBlur={props.handleBlur}
                labelName={t("promotion_discount_label")}
                type="number"
                id="promotionDiscount"
                name="promotionDiscount"
              />
              <CustomInput
                touched={props.touched.promotionNumberMonths}
                errors={props.errors.promotionNumberMonths}
                value={props.values.promotionNumberMonths}
                onChange={props.handleChange}
                onBlur={props.handleBlur}
                labelName={t("promotion_discount_months")}
                type="number"
                id="promotionNumberMonths"
                name="promotionNumberMonths"
              />
              <SubmitButton
                iconName="login"
                isLoading={isCreating}
                type="submit"
              >
                {t("create")}
              </SubmitButton>
            </StyledForm>
          )}
        </Formik>
      </div>
    </Drawer>
  )
}

PromotionDrawer.defaultProps = {
  open: false,
}

PromotionDrawer.propTypes = {
  firebase: PropTypes.shape().isRequired,
  open: PropTypes.bool,
  closeDrawer: PropTypes.func.isRequired,
}
export default withFirebase(PromotionDrawer)
